import { KitType } from '@repo/types';
import { Banner } from '@repo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  Card,
  ContainerSizes,
  FAQ,
  FeedbackWidget,
  Markdown,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useTrackPageView } from 'contexts/analytics/useTrackPageView';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useGutBodyConnections from 'hooks/useGutBodyConnections';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSingleConditionMetricsQuery,
  type SingleConditionData,
} from 'services/conditions/useConditionMetrics';
import useFeature from 'services/features/useFeature';
import { getShouldHideConsults } from 'store/kits';
import { FeedbackPageType } from 'types/Feedback';
import conditionSubject from 'utils/conditions/conditionSubject';
import formatConditionName from 'utils/conditions/formatConditionName';
import {
  MARKED_IN_SURVEY_DETAIL_LABEL,
  MarkedInSurveyBadge,
} from '../MarkedInSurveyBadge';
import SensitiveConditionsReminderModal from '../SensitiveConditionsReminderModal';
import { ConditionAssociationsCard } from './ConditionAssociationsCard';
import { ConditionDiveDeeperCard } from './ConditionDiveDeeperCard';
import { ConditionHowToUseCard } from './ConditionHowToUseCard';
import styles from './ConditionMetricsDetailView.module.css';
import { useConditionScrollDepth } from './hooks/useConditionScrollDepth';

const AboutHeading = ({ children }: { children: string }) => (
  <Typography variant='heading-m' className={styles.aboutCardTitle}>
    {children}
  </Typography>
);

export const ConditionMetricsDetailView = () => {
  const { currentKitId, currentKitType, currentKit, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const { condition } = useParams<{ condition: string }>();
  if (!condition) throw new Error('No condition provided');

  const { isFeatureEnabled: isSensitiveModalEnabled } = useFeature(
    'sensitive_conditions_reminder_modal',
  );
  const gutBodyConnections = useGutBodyConnections();

  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const hideConsults = useSelector(getShouldHideConsults(currentKitId));

  const { data: conditionData } = useSuspenseQuery<SingleConditionData | null>(
    getSingleConditionMetricsQuery(currentKitId, condition),
  );

  useTrackPageView(
    'Condition Card Viewed',
    currentKit && conditionData
      ? `${currentKitId}:${conditionData.condition}`
      : undefined,
    conditionData
      ? {
          condition_name: conditionData.condition_display_name,
          condition: conditionData.condition,
          marked_in_survey: conditionData.marked_in_survey,
          experience: 'gut_body_connections',
        }
      : undefined,
  );

  useConditionScrollDepth(conditionData?.condition);

  if (!currentKitId || !conditionData) return <Spinfinity />;

  const isGut = currentKitType === KitType.STOOL;

  const handleScheduleCall = () => {
    trackEvent('Schedule Call Clicked', {
      source: 'conditions_detail',
      condition: conditionData.condition,
      marked_in_survey: conditionData.marked_in_survey,
    });
    navigate(`/results/${currentKitId}/consults/select-method`);
  };

  const aside = (
    <div className={styles.aside}>
      <ConditionHowToUseCard kitId={currentKitId} />
      <ConditionDiveDeeperCard condition={conditionData.condition} />
    </div>
  );

  const formattedConditionName = formatConditionName(
    conditionData.condition_display_name,
  );

  return (
    <PageContainer size={ContainerSizes.LG}>
      {conditionData.sensitive && isSensitiveModalEnabled && (
        <SensitiveConditionsReminderModal condition={conditionData} />
      )}
      <div className={styles.header}>
        <Breadcrumb
          gutBodyConnections={gutBodyConnections}
          overrides={{
            [conditionData.condition]: conditionData.condition_display_name,
          }}
        />
        <div className={styles.titleRow}>
          <Typography variant='heading-xl'>
            {conditionData.condition_display_name}
          </Typography>
          {conditionData.marked_in_survey && (
            <MarkedInSurveyBadge
              className={styles.markedPill}
              label={MARKED_IN_SURVEY_DETAIL_LABEL}
            />
          )}
        </div>
        <Banner
          variant='full'
          title='This is not a diagnosis.'
          color='purple'
          content={`Research shows that ${formattedConditionName} is linked to ${isGut ? 'gut' : 'vaginal'} microbiome differences, but we don't know if one causes the other. Having microbiome associations does not mean ${conditionSubject(currentKit?.age_at_sampling, currentTinyAccount?.first_name)} or will develop ${formattedConditionName}.`}
        />
      </div>

      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <div className={styles.contentContainer}>
            <div className={styles.associationsGroup}>
              <ConditionAssociationsCard
                metrics={conditionData.metrics}
                samplingType={currentKitType}
                markedInSurvey={conditionData.marked_in_survey}
                hideConsults={hideConsults}
                onScheduleCall={handleScheduleCall}
                onNonFlaggedExpand={() =>
                  trackEvent('Non-Flagged Metrics Expanded', {
                    condition: conditionData.condition,
                  })
                }
              />

              <div className='hide-on-desktop'>{aside}</div>
            </div>

            {!!conditionData.about && (
              <Typography
                as='div'
                variant='body-s'
                className={styles.aboutCard}
              >
                <Markdown
                  customOverride={{
                    h2: AboutHeading,
                    h3: AboutHeading,
                    h4: AboutHeading,
                  }}
                >
                  {conditionData.about}
                </Markdown>
              </Typography>
            )}
            {conditionData.faq && (
              <Card variant='wrapper' className={styles.faqCard}>
                <Typography variant='heading-m'>
                  Frequently Asked Questions
                </Typography>
                <FAQ
                  faq={conditionData.faq}
                  hideLastDivider
                  hideFirstDivider
                  firstQuestionStartsOpen={false}
                />
              </Card>
            )}
            {conditionData.references && (
              <div className={styles.referenceCard}>
                <Typography
                  variant='heading-m'
                  className={styles.referenceCardTitle}
                >
                  References
                </Typography>
                <Typography as='div' variant='caption'>
                  <Markdown>{conditionData.references}</Markdown>
                </Typography>
              </div>
            )}
            <FeedbackWidget
              kitId={currentKitId}
              pageType={FeedbackPageType.CONDITION}
              pageId={conditionData.condition_id}
            />
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <div className='hide-on-mobile'>{aside}</div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
